import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Modal } from 'react-bootstrap'
import { Formik, Form } from 'formik'
import {
  Button,
  useErrorService,
  useNotificationService,
} from '@abroad/components'
import {
  formatDate,
  formatYear,
  getCurrentDate,
  getProgramDatesRange,
} from '../../utils/date'
import moment from 'moment-timezone'
import API from '../../utils/API'
import CurrencyInput from '../enterpriseSOW/CurrencyInput'
import {
  ExperienceCheckbox,
  ExperienceDatePicker,
  ExperienceSelect,
} from '../form'
import * as Yup from 'yup'
import {
  calculateDepositAmount as calculateQuestDepositAmount,
  getAutoDeductDays,
  getDefaultAutoDeductDateValue,
  getDepositPercentage,
  isDiscountProvided,
  stripCurrencyValue,
} from '../../utils/questPayment'

const AUTO_DEDUCT_DATE_FORMAT = 'MM/DD/YYYY'

const getAutoDeductHint = (defaultDays) => {
  if (!defaultDays) {
    return 'Default is with the deposit for this experience. Pick a date if the remaining balance should be charged later. If they pay by card, it is charged automatically on that date.'
  }
  return `Derived from the default of ${defaultDays} days before departure set for this experience. If they pay by card, the remaining balance is charged automatically on this date.`
}

const formatUsdLabel = (value) => {
  const n = Number(String(value ?? '').replace(/[^0-9.]/g, ''))
  if (!Number.isFinite(n) || n <= 0) return ''
  return `US$${n.toLocaleString('en-US')}`
}

const getQuestOptionLabel = (quest) => {
  const { start } = getProgramDatesRange(quest)
  return `${quest.name} : ${
    start ? moment.utc(start).format('MMMM') : quest?.month || ''
  } ${formatYear(start || quest?.startDate)}(${formatDate(
    start || quest?.startDate,
  )})`
}

const AddQuestModal = ({
  showModal,
  handleClose,
  updateClientProfile,
  questList,
}) => {
  const { clientId } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [selectedQuestId, setSelectedQuestId] = useState('')
  const Error = useErrorService()
  const Notification = useNotificationService()
  const [occupancy, setOccupancy] = useState('Double')
  const [singleSupplement, setSingleSupplement] = useState(false)
  // Once the admin types a deposit, price/occupancy/discount changes stop recalculating it
  const [depositTouched, setDepositTouched] = useState(false)

  const selectedQuest = questList.find((q) => q._id === selectedQuestId)
  const defaultAutoDeductDays = getAutoDeductDays(selectedQuest?.investments)
  const doubleRate = formatUsdLabel(selectedQuest?.investments?.standardRate)
  const singleRate = formatUsdLabel(
    selectedQuest?.investments?.singleSupplement,
  )

  const stripCurrency = (val) => {
    if (!val) return ''
    return stripCurrencyValue(val)
  }

  /** Deposit uses the selected experience's default deposit percentage. */
  const calculateDepositAmount = (actualCost, discountedCost, currentDeposit) =>
    depositTouched
      ? currentDeposit
      : calculateQuestDepositAmount(
          actualCost,
          discountedCost,
          getDepositPercentage(selectedQuest?.investments),
        )

  const validationSchema = Yup.object().shape({
    costActual: Yup.string()
      .nullable()
      .transform((value, originalValue) => (originalValue == null ? '' : value))
      .required('Actual amount is required'),
    costDeposit: Yup.string()
      .test(
        'deposit-lt-actual',
        'Deposit amount must be less than actual cost',
        function (value) {
          const { costActual } = this.parent
          const depositStr = value || ''
          const actualStr = costActual || ''
          if (!depositStr || !actualStr) return true
          const depositValue = Number(depositStr.replace(/[^0-9.]/g, ''))
          const actualValue = Number(actualStr.replace(/[^0-9.]/g, ''))
          return depositValue <= actualValue
        },
      )
      .test(
        'deposit-lt-eq-discount',
        'Deposit amount must be less than or equal to discount amount',
        function (value) {
          const { costDiscount, applyDiscount } = this.parent
          if (
            !applyDiscount ||
            !isDiscountProvided(value) ||
            !isDiscountProvided(costDiscount)
          ) {
            return true
          }
          const depositValue = stripCurrencyValue(value)
          const discountValue = stripCurrencyValue(costDiscount)
          return depositValue <= discountValue
        },
      ),
    costDiscount: Yup.string()
      .test(
        'discount-lt-actual',
        'Discount amount must be less than actual cost',
        function (value) {
          const { costActual, applyDiscount } = this.parent
          if (
            !applyDiscount ||
            !isDiscountProvided(value) ||
            !isDiscountProvided(costActual)
          ) {
            return true
          }
          const discountValue = stripCurrencyValue(value)
          const actualValue = stripCurrencyValue(costActual)
          return discountValue <= actualValue
        },
      )
      .test(
        'discount-non-negative',
        'Discount amount cannot be negative',
        function (value) {
          if (!this.parent.applyDiscount || !isDiscountProvided(value)) {
            return true
          }
          return stripCurrencyValue(value) >= 0
        },
      ),
    autoDeductDate: Yup.string()
      .nullable()
      .test(
        'auto-deduct-date-required',
        'Remaining balance due date is required',
        (value) => !!value || !getDefaultAutoDeductDateValue(selectedQuest),
      )
      .test(
        'auto-deduct-date-valid',
        'Enter a valid date',
        (value) =>
          !value || moment(value, AUTO_DEDUCT_DATE_FORMAT, true).isValid(),
      ),
  })

  const handelForceAssignUser = async (values) => {
    setIsLoading(true)
    const accommodation = {
      occupancy: occupancy.toLowerCase(),
      singleSupplement,
    }
    const applyDiscount = !!values.applyDiscount
    const pricing = {
      actualCost: stripCurrency(values.costActual) || 0,
      discountedCost:
        applyDiscount && isDiscountProvided(values.costDiscount)
          ? stripCurrencyValue(values.costDiscount)
          : null,
      discountReason: applyDiscount ? values.costDiscountReason || '' : '',
      depositAmount: isDiscountProvided(values.costDeposit)
        ? stripCurrencyValue(values.costDeposit)
        : 0,
      // null = collect the remaining balance with the deposit
      autoDeductDate: values.autoDeductDate
        ? moment
            .utc(values.autoDeductDate, AUTO_DEDUCT_DATE_FORMAT)
            .format('YYYY-MM-DD')
        : null,
    }
    try {
      const { data } = await API.AdminAPI.quests.addUserQuest(selectedQuestId, {
        userId: clientId,
        accommodation,
        pricing,
      })

      if (data) {
        Notification.showNotification(
          'Experience has been added successfully',
          'success',
        )
        setIsLoading(false)
        setDepositTouched(false)
        updateClientProfile()
        handleClose()
      }
    } catch (e) {
      Error.showError(e)
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (questList.length > 0) {
      setSelectedQuestId(questList[0]._id)
    }
  }, [questList])

  useEffect(() => {
    if (showModal) {
      setDepositTouched(false)
      setOccupancy('Double')
      setSingleSupplement(false)
    }
  }, [showModal])

  const getOccupancyRate = (selectedOccupancy) => {
    if (selectedOccupancy === 'Single') {
      return selectedQuest?.investments?.singleSupplement ?? 0
    }
    return selectedQuest?.investments?.standardRate ?? 0
  }

  const questOptions = questList.map((quest) => ({
    value: quest._id,
    label: getQuestOptionLabel(quest),
  }))

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      centered
      dialogClassName='experience-add-part-dialog'
      contentClassName='experience-add-part-card is-scroll'>
      <Modal.Body className='p-0'>
        <div className='experience-add-part'>
          <h2>Add to experience</h2>
          <p className='experience-add-part-lead'>
            Set occupancy and price for this person.
          </p>

          <Formik
            initialValues={{
              costActual: String(
                selectedQuest?.investments?.standardRate ?? '',
              ),
              costDeposit: calculateQuestDepositAmount(
                selectedQuest?.investments?.standardRate,
                '',
                getDepositPercentage(selectedQuest?.investments),
              ),
              costDiscount: '',
              costDiscountReason: '',
              applyDiscount: false,
              autoDeductDate: getDefaultAutoDeductDateValue(selectedQuest),
            }}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={handelForceAssignUser}>
            {({
              handleSubmit,
              errors,
              touched,
              values,
              handleChange,
              handleBlur,
              setFieldValue,
              setFieldTouched,
              setValues,
              validateForm,
            }) => (
              <Form onSubmit={handleSubmit}>
                <div className='experience-add-part-fields'>
                  <ExperienceSelect
                    id='addQuestSelect'
                    label='Experience'
                    value={selectedQuestId}
                    options={questOptions}
                    isSearchable
                    menuPlacement='auto'
                    onChange={(e) => {
                      setSelectedQuestId(e.target.value)
                      setDepositTouched(false)
                      setOccupancy('Double')
                      setSingleSupplement(false)
                    }}
                  />

                  <ExperienceSelect
                    id='addQuestOccupancy'
                    label='Occupancy'
                    value={occupancy}
                    options={[
                      {
                        value: 'Double',
                        label: doubleRate ? `Double · ${doubleRate}` : 'Double',
                      },
                      {
                        value: 'Single',
                        label: singleRate ? `Single · ${singleRate}` : 'Single',
                      },
                    ]}
                    isSearchable={false}
                    onChange={(e) => {
                      const selected = e.target.value
                      setOccupancy(selected)
                      setSingleSupplement(selected === 'Single')

                      const newCost = getOccupancyRate(selected)

                      setValues({
                        ...values,
                        costActual: String(newCost),
                        costDeposit: calculateDepositAmount(
                          newCost,
                          values.applyDiscount ? values.costDiscount : '',
                          values.costDeposit,
                        ),
                      })

                      setTimeout(() => {
                        setFieldTouched('costActual', true, false)
                        setFieldTouched('costDiscount', true, false)
                        setFieldTouched('costDeposit', true, false)
                        validateForm().catch(console.error)
                      }, 0)
                    }}
                  />

                  <div className='experience-f'>
                    <label className='experience-f-label'>Price</label>
                    <CurrencyInput
                      inputClasses='form-control experience-f-control'
                      name='costActual'
                      value={values.costActual}
                      onChange={(e) => {
                        const nextActual = e.target.value
                        setValues({
                          ...values,
                          costActual: nextActual,
                          costDeposit: calculateDepositAmount(
                            nextActual,
                            values.applyDiscount ? values.costDiscount : '',
                            values.costDeposit,
                          ),
                        })
                      }}
                      onBlur={handleBlur}
                    />
                    {touched.costActual && errors.costActual ? (
                      <p className='experience-add-part-error'>
                        {errors.costActual}
                      </p>
                    ) : null}
                  </div>

                  <ExperienceCheckbox
                    name='applyDiscount'
                    label='Apply discount'
                    checked={!!values.applyDiscount}
                    onChange={(e) => {
                      const checked = e.target.checked
                      if (checked) {
                        setFieldValue('applyDiscount', true)
                      } else {
                        setValues({
                          ...values,
                          applyDiscount: false,
                          costDiscount: '',
                          costDiscountReason: '',
                          costDeposit: calculateDepositAmount(
                            values.costActual,
                            '',
                            values.costDeposit,
                          ),
                        })
                      }
                    }}
                  />

                  {values.applyDiscount ? (
                    <div className='experience-discount-fields'>
                      <div className='experience-f'>
                        <label className='experience-f-label'>
                          Discounted price
                        </label>
                        <CurrencyInput
                          inputClasses='form-control experience-f-control'
                          name='costDiscount'
                          onChange={(e) => {
                            const nextDiscount = e.target.value
                            setValues({
                              ...values,
                              costDiscount: nextDiscount,
                              costDeposit: calculateDepositAmount(
                                values.costActual,
                                nextDiscount,
                                values.costDeposit,
                              ),
                            })
                          }}
                        />
                        <p className='experience-f-hint text-dark'>
                          Amount they will pay after the discount.
                        </p>
                        {touched.costDiscount && errors.costDiscount ? (
                          <p className='experience-add-part-error'>
                            {errors.costDiscount}
                          </p>
                        ) : null}
                      </div>
                      <div className='experience-f'>
                        <label className='experience-f-label'>
                          Discount reason
                        </label>
                        <input
                          className='form-control experience-f-control'
                          type='text'
                          name='costDiscountReason'
                          value={values.costDiscountReason || ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          placeholder='Optional'
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className='experience-f'>
                    <label className='experience-f-label'>Deposit</label>
                    <CurrencyInput
                      inputClasses='form-control experience-f-control'
                      name='costDeposit'
                      value={values.costDeposit}
                      onChange={(e) => {
                        setDepositTouched(true)
                        setFieldValue('costDeposit', e.target.value)
                      }}
                      onBlur={handleBlur}
                    />
                    {touched.costDeposit && errors.costDeposit ? (
                      <p className='experience-add-part-error'>
                        {errors.costDeposit}
                      </p>
                    ) : null}
                  </div>

                  <div className='experience-f'>
                    <ExperienceDatePicker
                      name='autoDeductDate'
                      label='Remaining balance due'
                      labelProps={{ className: 'experience-f-label' }}
                      placeholder='Add date'
                      minimumDate={getCurrentDate()}
                      className='experience-f-control'
                      displayFlex={false}
                      position='auto'
                    />
                    <p className='experience-f-hint text-dark'>
                      {getAutoDeductHint(defaultAutoDeductDays)}
                    </p>
                    {touched.autoDeductDate && errors.autoDeductDate ? (
                      <p className='experience-add-part-error'>
                        {errors.autoDeductDate}
                      </p>
                    ) : null}
                  </div>
                </div>

                <div className='experience-modal-actions'>
                  <button
                    type='button'
                    className='btn-outline-experience'
                    onClick={() => {
                      handleClose()
                      setSelectedQuestId('')
                      setDepositTouched(false)
                    }}
                    disabled={isLoading}>
                    Cancel
                  </button>
                  <Button
                    type='submit'
                    variant='saffron'
                    size='sm'
                    disabled={
                      !selectedQuestId ||
                      !values.costActual ||
                      !!errors.costActual ||
                      !!errors.costDeposit ||
                      !!errors.costDiscount ||
                      !!errors.autoDeductDate ||
                      isLoading
                    }
                    isLoading={isLoading}
                    className='btn-navy border-0'>
                    <span>Add</span>
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  )
}

export default AddQuestModal
